import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { PageHeader, Btn, Card, Modal, Input, Textarea, Empty, Spinner, useToast } from '../components/shared/UI';
import { fmtCurrency } from '../utils/helpers';
import { useT } from '../i18n';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const IS_NATIVE = Capacitor.isNativePlatform();

const empty = {
  description: '', unitPrice: 0, costPrice: 0, unit: '', category: '',
  sku: '', barcode: '', brand: '', supplier: '', taxRate: 0, stockQuantity: 0, notes: '',
};

const ACCEPT = '.pdf,.png,.jpg,.jpeg,.gif,.webp,.xlsx,.xls,.csv,application/pdf,image/*,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv';

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('Could not read file'));
  reader.onload = () => {
    const result = reader.result || '';
    const comma = result.indexOf(',');
    resolve(comma >= 0 ? result.slice(comma + 1) : result);
  };
  reader.readAsDataURL(file);
});

export default function Items() {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(empty);
  const [saving, setSaving]   = useState(false);
  const [search, setSearch]   = useState('');
  const [companySymbol, setCompanySymbol] = useState('RM');
  const { show, ToastContainer } = useToast();
  const { user } = useAuth();
  const { t } = useT();

  const [importOpen, setImportOpen] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted]   = useState([]);
  const [selected, setSelected]     = useState({});
  const [importError, setImportError] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    Promise.all([api.get('/items'), api.get('/company')])
      .then(([its, co]) => {
        setItems(its.data);
        if (co.data?.currencySymbol) setCompanySymbol(co.data.currencySymbol);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(i =>
      i.description.toLowerCase().includes(q) ||
      (i.category || '').toLowerCase().includes(q) ||
      (i.unit || '').toLowerCase().includes(q) ||
      (i.sku || '').toLowerCase().includes(q) ||
      (i.supplier || '').toLowerCase().includes(q) ||
      (i.brand || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const openNew  = ()  => { setEditing(null); setForm(empty); setModal(true); };
  const openEdit = (it) => {
    setEditing(it._id);
    setForm({
      description: it.description, unitPrice: it.unitPrice, costPrice: it.costPrice || 0,
      unit: it.unit || '', category: it.category || '', sku: it.sku || '',
      barcode: it.barcode || '', brand: it.brand || '', supplier: it.supplier || '',
      taxRate: it.taxRate || 0, stockQuantity: it.stockQuantity || 0, notes: it.notes || '',
    });
    setModal(true);
  };

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const numericFields = ['unitPrice', 'costPrice', 'taxRate', 'stockQuantity'];
  const normalize = (raw) => {
    const out = { ...raw };
    numericFields.forEach(k => { out[k] = Number(out[k]) || 0; });
    return out;
  };

  const save = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = normalize(form);
      if (editing) {
        const { data } = await api.put(`/items/${editing}`, payload);
        setItems(xs => xs.map(x => x._id === editing ? data : x));
      } else {
        const { data } = await api.post('/items', payload);
        setItems(xs => [...xs, data].sort((a, b) => a.description.localeCompare(b.description)));
      }
      show(editing ? t('items.updated') : t('items.added'));
      setModal(false);
    } catch { show(t('items.errorSaving'), 'error'); }
    finally { setSaving(false); }
  };

  const del = async id => {
    if (!window.confirm(t('items.confirmDelete'))) return;
    await api.delete(`/items/${id}`);
    setItems(xs => xs.filter(x => x._id !== id));
    show(t('items.deleted'));
  };

  // ── Import flow ─────────────────────────────────────────────────────────
  // AI OCR import is a paid feature; trial users see the button but get an upgrade hint
  const aiAllowed = user?.hasAiAccess !== false;
  const openImport = () => {
    if (!aiAllowed) {
      show(t('items.aiPaidOnly'), 'error');
      return;
    }
    setExtracted([]); setSelected({}); setImportError(''); setImportOpen(true);
  };

  const runExtract = async ({ filename, mimeType, data }) => {
    setImportError(''); setExtracted([]); setSelected({}); setExtracting(true);
    try {
      const { data: resp } = await api.post('/items/extract', { filename, mimeType, data });
      const rows = resp.items || [];
      setExtracted(rows);
      setSelected(Object.fromEntries(rows.map((_, i) => [i, true])));
      if (rows.length === 0) setImportError(t('items.noneDetected'));
    } catch (err) {
      setImportError(err.response?.data?.message || err.message || t('items.extractionFailed'));
    } finally {
      setExtracting(false);
    }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await fileToBase64(file);
      await runExtract({ filename: file.name, mimeType: file.type, data });
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onTakePhoto = async () => {
    try {
      const photo = await Camera.getPhoto({
        quality: 85,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Camera,
        saveToGallery: false,
      });
      if (!photo?.base64String) return;
      await runExtract({
        filename: `photo.${photo.format || 'jpg'}`,
        mimeType: `image/${photo.format === 'png' ? 'png' : 'jpeg'}`,
        data: photo.base64String,
      });
    } catch (err) {
      // User cancelled the camera — silent
      const msg = err?.message || '';
      if (/cancel/i.test(msg)) return;
      setImportError(msg || t('items.cameraFailed'));
    }
  };

  const updateExtracted = (i, k, v) =>
    setExtracted(xs => xs.map((x, idx) => idx === i ? { ...x, [k]: v } : x));

  const toggleAll = (checked) =>
    setSelected(Object.fromEntries(extracted.map((_, i) => [i, checked])));

  const saveSelected = async () => {
    const chosen = extracted
      .map((row, i) => ({ row, keep: selected[i] }))
      .filter(x => x.keep && x.row.description?.trim())
      .map(x => normalize(x.row));
    if (chosen.length === 0) {
      show(t('items.selectAtLeastOne'), 'error');
      return;
    }
    setExtracting(true);
    try {
      const { data } = await api.post('/items/bulk', { items: chosen });
      setItems(xs => [...xs, ...data].sort((a, b) => a.description.localeCompare(b.description)));
      show(t('items.imported', { n: data.length }));
      setImportOpen(false);
    } catch (err) {
      show(err.response?.data?.message || t('items.importFailed'), 'error');
    } finally {
      setExtracting(false);
    }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div>
      <ToastContainer />
      <PageHeader
        title={t('items.title')}
        subtitle={t('items.count', { n: items.length })}
        actions={
          <>
            <Btn variant="ghost" onClick={openImport} style={aiAllowed ? undefined : { opacity: 0.5 }}>
              📄 {t('items.importBtn')}{aiAllowed ? '' : ' 🔒'}
            </Btn>
            <Btn variant="primary" onClick={openNew}>+ {t('items.addItem')}</Btn>
          </>
        }
      />

      {!loading && items.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t('items.searchPh')} />
        </div>
      )}

      {loading ? <Spinner /> : items.length === 0
        ? <Empty icon="📦"
            message={t('items.empty')}
            action={<Btn variant="primary" onClick={openNew}>{t('items.addItem')}</Btn>} />
        : filtered.length === 0
          ? <Empty icon="🔍" message={t('items.noMatch', { q: search })} />
          : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {filtered.map(it => (
                <Card key={it._id} style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: 12, background: 'var(--light-blue)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 18, flexShrink: 0
                    }}>📦</div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', lineHeight: 1.35, wordBreak: 'break-word' }}>
                        {it.description}
                      </p>
                      {it.category && (
                        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{it.category}</p>
                      )}
                      {it.sku && (
                        <p style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 2 }}>SKU: {it.sku}</p>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.7, marginBottom: 14, borderTop: '1px solid var(--border-light)', paddingTop: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <span>{t('items.unitPrice')}</span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--heading)' }}>
                        {fmtCurrency(it.unitPrice, companySymbol)}
                      </span>
                    </div>
                    {it.unit && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{t('items.unit')}</span><span>{it.unit}</span>
                      </div>
                    )}
                    {!!it.stockQuantity && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{t('items.inStock')}</span><span>{it.stockQuantity}</span>
                      </div>
                    )}
                    {it.supplier && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{t('items.supplier')}</span><span>{it.supplier}</span>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
                    <Btn size="sm" variant="ghost" onClick={() => openEdit(it)} style={{ flex: 1, justifyContent: 'center' }}>{t('common.edit')}</Btn>
                    <Btn size="sm" variant="danger" onClick={() => del(it._id)}>{t('common.delete')}</Btn>
                  </div>
                </Card>
              ))}
            </div>
          )
      }

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? t('items.editItem') : t('items.addItem')} width={680}>
        <form onSubmit={save} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Input label={t('items.description')} value={form.description} onChange={set('description')} required
            placeholder={t('items.descriptionPh')} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Input label={t('items.unitPriceLabel', { s: companySymbol })} type="number" min="0" step="0.01"
              value={form.unitPrice} onChange={set('unitPrice')} required />
            <Input label={t('items.costPriceLabel', { s: companySymbol })} type="number" min="0" step="0.01"
              value={form.costPrice} onChange={set('costPrice')} />
            <Input label={t('items.taxRate')} type="number" min="0" step="0.01"
              value={form.taxRate} onChange={set('taxRate')} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Input label={t('items.unitLabel')} value={form.unit} onChange={set('unit')} placeholder={t('items.unitPh')} />
            <Input label={t('items.category')} value={form.category} onChange={set('category')} placeholder={t('items.categoryPh')} />
            <Input label={t('items.stockQty')} type="number" min="0" step="1"
              value={form.stockQuantity} onChange={set('stockQuantity')} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label={t('items.sku')} value={form.sku} onChange={set('sku')} placeholder={t('items.skuPh')} />
            <Input label={t('items.barcode')} value={form.barcode} onChange={set('barcode')} placeholder={t('items.barcodePh')} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label={t('items.brand')} value={form.brand} onChange={set('brand')} />
            <Input label={t('items.supplier')} value={form.supplier} onChange={set('supplier')} />
          </div>
          <Textarea label={t('items.notes')} value={form.notes} onChange={set('notes')} rows={2}
            placeholder={t('items.notesPh')} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Btn variant="ghost" type="button" onClick={() => setModal(false)}>{t('common.cancel')}</Btn>
            <Btn variant="primary" type="submit" disabled={saving}>
              {saving ? t('common.saving') : editing ? t('editor.saveChanges') : t('items.addItem')}
            </Btn>
          </div>
        </form>
      </Modal>

      <Modal open={importOpen} onClose={() => !extracting && setImportOpen(false)}
        title={t('items.importTitle')} width={1000}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {t('items.importIntro')}
          </p>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              onChange={onPickFile}
              disabled={extracting}
              style={{ fontSize: 13 }}
            />
            {IS_NATIVE && (
              <Btn variant="primary" onClick={onTakePhoto} disabled={extracting}>
                📷 {t('items.takePhoto')}
              </Btn>
            )}
            {extracting && <Spinner />}
          </div>

          {importError && (
            <div style={{
              padding: '10px 12px', background: 'var(--danger-bg, #fee)', border: '1px solid #f5c6c6',
              borderRadius: 8, color: '#a33', fontSize: 13
            }}>{importError}</div>
          )}

          {extracted.length > 0 && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: 14 }}>
                  {t('items.foundSelected', { n: extracted.length, c: selectedCount })}
                </strong>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Btn size="sm" variant="ghost" onClick={() => toggleAll(true)}>{t('items.selectAll')}</Btn>
                  <Btn size="sm" variant="ghost" onClick={() => toggleAll(false)}>{t('items.clear')}</Btn>
                </div>
              </div>

              <div style={{ overflow: 'auto', maxHeight: '50vh', border: '1px solid var(--border)', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead style={{ position: 'sticky', top: 0, background: 'var(--card-bg)', zIndex: 1 }}>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <th style={th}></th>
                      <th style={th}>{t('items.thDescription')}</th>
                      <th style={th}>{t('items.thUnitPrice')}</th>
                      <th style={th}>{t('items.thCost')}</th>
                      <th style={th}>{t('items.thUnit')}</th>
                      <th style={th}>{t('items.thSku')}</th>
                      <th style={th}>{t('items.thCategory')}</th>
                      <th style={th}>{t('items.thBrand')}</th>
                      <th style={th}>{t('items.thSupplier')}</th>
                      <th style={th}>{t('items.thTax')}</th>
                      <th style={th}>{t('items.thQty')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {extracted.map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border-light)' }}>
                        <td style={td}>
                          <input type="checkbox" checked={!!selected[i]}
                            onChange={e => setSelected(s => ({ ...s, [i]: e.target.checked }))} />
                        </td>
                        <td style={td}><input style={cellInput} value={row.description || ''} onChange={e => updateExtracted(i, 'description', e.target.value)} /></td>
                        <td style={td}><input style={cellNum} type="number" step="0.01" value={row.unitPrice ?? 0} onChange={e => updateExtracted(i, 'unitPrice', e.target.value)} /></td>
                        <td style={td}><input style={cellNum} type="number" step="0.01" value={row.costPrice ?? 0} onChange={e => updateExtracted(i, 'costPrice', e.target.value)} /></td>
                        <td style={td}><input style={cellShort} value={row.unit || ''} onChange={e => updateExtracted(i, 'unit', e.target.value)} /></td>
                        <td style={td}><input style={cellShort} value={row.sku || ''} onChange={e => updateExtracted(i, 'sku', e.target.value)} /></td>
                        <td style={td}><input style={cellShort} value={row.category || ''} onChange={e => updateExtracted(i, 'category', e.target.value)} /></td>
                        <td style={td}><input style={cellShort} value={row.brand || ''} onChange={e => updateExtracted(i, 'brand', e.target.value)} /></td>
                        <td style={td}><input style={cellShort} value={row.supplier || ''} onChange={e => updateExtracted(i, 'supplier', e.target.value)} /></td>
                        <td style={td}><input style={cellNum} type="number" step="0.01" value={row.taxRate ?? 0} onChange={e => updateExtracted(i, 'taxRate', e.target.value)} /></td>
                        <td style={td}><input style={cellNum} type="number" step="1" value={row.stockQuantity ?? 0} onChange={e => updateExtracted(i, 'stockQuantity', e.target.value)} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn variant="ghost" type="button" onClick={() => setImportOpen(false)} disabled={extracting}>{t('common.cancel')}</Btn>
            <Btn variant="primary" onClick={saveSelected}
              disabled={extracting || extracted.length === 0 || selectedCount === 0}>
              {extracting ? t('items.working') : t('items.saveCount', { n: selectedCount })}
            </Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}

const th = { textAlign: 'left', padding: '8px 6px', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' };
const td = { padding: '4px 6px', verticalAlign: 'middle' };
const cellBase = {
  width: '100%', padding: '4px 6px', fontSize: 12,
  border: '1px solid var(--border-light)', borderRadius: 4,
  background: 'var(--card-bg)', color: 'var(--text)', fontFamily: 'inherit',
};
const cellInput = { ...cellBase, minWidth: 180 };
const cellShort = { ...cellBase, minWidth: 80 };
const cellNum   = { ...cellBase, minWidth: 70, textAlign: 'right' };
